import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AddBankArgs {
  bankName:      string;
  accountNumber: string;
  ifscCode:      string;
  branchCity?:   string;
  branchIds:     string | string[];
}

interface UpdateBankArgs extends Partial<AddBankArgs> {
  id: string;
}

interface GetBanksParams {
  search?: string;
  status?: 'all' | 'active' | 'inactive';
  page?:   number;
  limit?:  number;
}

/* ─── API ────────────────────────────────────────────────────────────── */

export const banksApi = createApi({
  reducerPath: 'banksApi',
  baseQuery:   dynamicBaseQuery,
  tagTypes:    ['Bank'] as const,
  endpoints:   (builder) => ({
    getBanks: builder.query<unknown, GetBanksParams | void>({
      query: (params) => {
        const { search, status, page, limit } = params || {};
        const qp = new URLSearchParams();
        if (search)          qp.append('search', search);
        if (status)          qp.append('status', status);
        if (page  != null)   qp.append('page', String(page));
        if (limit != null)   qp.append('limit', String(limit));
        const qs = qp.toString();
        return { url: qs ? `/banks?${qs}` : `/banks`, method: 'GET' };
      },
      providesTags: ['Bank'],
    }),
    getBankById: builder.query<unknown, string>({
      query: (id) => ({ url: `/banks/${id}`, method: 'GET' }),
      providesTags: ['Bank'],
    }),
    getOpenBanks: builder.query<unknown, void>({
      query: () => ({ url: `/banks/open`, method: 'GET' }),
      providesTags: ['Bank'],
    }),
    addBank: builder.mutation<unknown, AddBankArgs>({
      query: (body) => ({ url: `/banks`, method: 'POST', body }),
      invalidatesTags: ['Bank'],
    }),
    updateBank: builder.mutation<unknown, UpdateBankArgs>({
      query: ({ id, ...body }) => ({ url: `/banks/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Bank'],
    }),
    toggleBankStatus: builder.mutation<unknown, { id: string; isActive: boolean }>({
      query: ({ id, isActive }) => ({ url: `/banks/${id}/status`, method: 'PATCH', body: { isActive } }),
      invalidatesTags: ['Bank'],
    }),
  }),
});

export const {
  useGetBanksQuery,
  useGetBankByIdQuery,
  useGetOpenBanksQuery,
  useAddBankMutation,
  useUpdateBankMutation,
  useToggleBankStatusMutation,
} = banksApi;
